import Breadcrumbs from 'shell/components/shared/breadcrumb';
import ListHeader from 'shell/components/shared/list-header';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { typeDestinationCrumbs } from '../constants';
import { authsObj, headCells, validationSchemaTypesDestinationFilterForm } from './constants';
import { generatePath, useNavigate } from 'react-router-dom';
import { initialValuesTypeDestination } from 'system/pages/type-destination/index';
import { TypeDestinationClient, typesDestinationFormData } from './types';
import { useFormik } from 'formik';
import TypeDestinationFilters from 'system/components/type-destination/list/filters';
import { useEffect, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { useCallback } from 'react';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { HttpResponseList } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { TypeDestination } from 'shell/models/type-destination';
import {
  DestinationResponse,
  transformerTypeDestinations,
} from '../utils/transformer-type-destinations';
import { __OPERATION__EVENT__ADMIN__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { AxiosPromise } from 'axios';
import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Typography } from '@mui/material';
const TypeDestinationListPage = (): React.ReactElement => {
  const auths = useActionAuthorization(authsObj);
  const navigate = useNavigate();
  const filterTable = useFilterTable<typesDestinationFormData>();

  const [typeDestinations, getTypeDestinations] = useHttpFetch<
    HttpResponseList<TypeDestination>,
    DestinationResponse
  >(`${__OPERATION__EVENT__ADMIN__}/tipoDestinazione/list`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerTypeDestinations,
  });

  const deleteTypeDestination = useHttpClient(`${__OPERATION__EVENT__ADMIN__}/tipoDestinazione`, {
    options: {
      method: 'DELETE',
    },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<TypeDestinationClient>>({
    element: null,
    open: false,
  });
  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<TypeDestinationClient>>({
    element: null,
    open: false,
  });

  const onSubmit = useCallback(
    (values: typesDestinationFormData) => {
      const queryString = transformDataset({ ...values, page: 0 });
      filterTypeDestinationForm.setFieldValue('page', 0);
      getTypeDestinations({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getTypeDestinations]
  );

  const filterTypeDestinationForm = useFormik({
    initialValues: initialValuesTypeDestination,
    validationSchema: validationSchemaTypesDestinationFilterForm,
    onSubmit,
  });

  useEffect(() => {
    filterTypeDestinationForm.setValues(filterTable.values || initialValuesTypeDestination);
    const queryString = transformDataset(filterTable.values || initialValuesTypeDestination);
    getTypeDestinations({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  const onTrashPress = useCallback(
    (cell): Promise<TypeDestinationClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<TypeDestinationClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = { ...filterTypeDestinationForm.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getTypeDestinations({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TypeDestination>, DestinationResponse>
      ).then(() => {
        filterTypeDestinationForm.setFieldValue('page', newPage);
      });
    },
    [getTypeDestinations, filterTypeDestinationForm.values, filterTypeDestinationForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterTypeDestinationForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      const queryString = transformDataset(newDataset);
      (
        getTypeDestinations({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TypeDestination>, DestinationResponse>
      ).then(() => {
        filterTypeDestinationForm.setValues(newDataset);
      });
    },
    [getTypeDestinations, filterTypeDestinationForm.setFieldValue, filterTypeDestinationForm.values]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<TypeDestinationClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/eventmanager/tipidestinazione/dettaglio/:id', {
            id: cell.id ? cell.id : cell.code,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<TypeDestinationClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/eventmanager/tipidestinazione/modifica/:id', {
            id: cell.id ? cell.id : cell.code,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterTypeDestinationForm.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getTypeDestinations({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TypeDestination>, DestinationResponse>
      ).then(() => {
        filterTypeDestinationForm.setFieldValue('orders', orders);
      });
    },
    [getTypeDestinations, filterTypeDestinationForm.values, filterTypeDestinationForm.setValues]
  );

  const count: number = useMemo(() => {
    return (typeDestinations.data as DestinationResponse)?.pageInfo?.totalCount || 0;
  }, [typeDestinations.data]);
  return (
    <>
      <Breadcrumbs items={typeDestinationCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo Tipo/Destinazione"
        addBtnLabel="Aggiungi nuovo Tipo/Destinazione"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/eventmanager/tipidestinazione/nuovo');
        }}
        title="Gestione Tipo/Destinazione"
      />
      <TypeDestinationFilters filterTypeDestinationForm={filterTypeDestinationForm} />
      {typeDestinations?.data ? (
        (typeDestinations?.data as DestinationResponse)?.typeDestinations?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterTypeDestinationForm.values.orders}
            activeActions={auths}
            page={filterTypeDestinationForm.values.page}
            rowsPerPage={filterTypeDestinationForm.values.rowsPerPage}
            body={(typeDestinations?.data as DestinationResponse)?.typeDestinations}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            isServer
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista tipo destinazione"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Tipo/Destinazione"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteTypeDestination({
              url: `/${deleteAlertModal.element?.code}`,
            }) as AxiosPromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Tipo/Destinazione"
        message={`La cancellazione del Destinazione ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const newDataset = {
            ...filterTypeDestinationForm.values,
            page: 0,
          };
          const queryString = transformDataset(newDataset);
          getTypeDestinations({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          const newDataset = {
            ...filterTypeDestinationForm.values,
            page: 0,
          };
          const queryString = transformDataset(newDataset);
          getTypeDestinations({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </>
  );
};

TypeDestinationListPage.displayName = 'Type destination list page';
export default TypeDestinationListPage;
